import type { Rule } from './types';

function wordCount(text: string): number {
  return text.split(/\s+/).filter(Boolean).length;
}

function hasDir(ctx: { directoryListing?: string[] }, name: string): boolean {
  return (ctx.directoryListing ?? []).some((e) => e.replace(/\/$/, '') === name);
}

function countLongCodeBlocks(body: string): number {
  const fence = /^```[\s\S]*?^```/gm;
  let long = 0;
  let m: RegExpExecArray | null;
  while ((m = fence.exec(body)) !== null) {
    if (m[0].split('\n').length >= 15) long++;
  }
  return long;
}

export const progressiveDisclosureRules: Rule[] = [
  {
    id: 'pd.uses-references-when-large',
    category: 'progressiveDisclosure',
    severity: 'warn',
    weight: 4,
    title: 'Uses `references/` when body is large',
    appliesTo: 'both',
    run(ctx) {
      const words = wordCount(ctx.parsed.body);
      if (words <= 2500) return { status: 'pass', message: 'Body small enough to inline.' };
      const hasRefDir = ctx.source === 'github' ? hasDir(ctx, 'references') : false;
      const linksRefs = /references\/[A-Za-z0-9._-]+/.test(ctx.parsed.body);
      if (hasRefDir || linksRefs) {
        return { status: 'pass', message: 'Large body, references in use.' };
      }
      return {
        status: 'warn',
        message: `Body is ${words} words but no \`references/\` is in use.`,
        fix: 'Move details out of SKILL.md into `references/` and link to them.',
      };
    },
  },
  {
    id: 'pd.uses-scripts-when-codey',
    category: 'progressiveDisclosure',
    severity: 'info',
    weight: 2,
    title: 'Uses `scripts/` for long code',
    appliesTo: 'both',
    run(ctx) {
      const longBlocks = countLongCodeBlocks(ctx.parsed.body);
      if (longBlocks < 3) return { status: 'pass', message: 'Inline code is light.' };
      const hasScripts = ctx.source === 'github' ? hasDir(ctx, 'scripts') : false;
      const linksScripts = /scripts\/[A-Za-z0-9._-]+/.test(ctx.parsed.body);
      if (hasScripts || linksScripts) {
        return { status: 'pass', message: 'Scripts directory in use.' };
      }
      return {
        status: 'warn',
        message: `${longBlocks} long inline code blocks without a \`scripts/\` directory.`,
        fix: 'Extract long code into `scripts/` and have Claude invoke them.',
      };
    },
  },
  {
    id: 'pd.references-linked',
    category: 'progressiveDisclosure',
    severity: 'info',
    weight: 2,
    title: '`references/` is linked from the body',
    appliesTo: 'github',
    run(ctx) {
      if (!hasDir(ctx, 'references')) {
        return { status: 'skipped', message: 'No references/ directory.' };
      }
      if (/references\/[A-Za-z0-9._-]+/.test(ctx.parsed.body)) {
        return { status: 'pass', message: 'Body links into references/.' };
      }
      return {
        status: 'warn',
        message: '`references/` exists but SKILL.md does not link into it.',
        fix: 'Add a line like `See `references/api-guide.md` for…`.',
      };
    },
  },
  {
    id: 'pd.references-not-empty',
    category: 'progressiveDisclosure',
    severity: 'warn',
    weight: 2,
    title: 'Linked references/ files are not empty',
    appliesTo: 'github',
    run(ctx) {
      const entries = ctx.subfolderEntries?.['references'];
      if (!entries) return { status: 'skipped', message: 'No references/ folder.' };
      const tiny = entries.filter((e) => e.type === 'file' && (e.size ?? 0) < 200);
      if (tiny.length === 0) return { status: 'pass', message: `${entries.length} reference file(s), all substantive.` };
      return {
        status: 'warn',
        message: `${tiny.length} reference file(s) under 200 bytes: ${tiny.map((t) => t.name).join(', ')}.`,
        fix: 'Empty or stub reference files signal incomplete progressive disclosure. Either fill them or remove them.',
      };
    },
  },
  {
    id: 'pd.scripts-executable',
    category: 'progressiveDisclosure',
    severity: 'warn',
    weight: 2,
    title: 'scripts/ contains non-trivial code',
    appliesTo: 'github',
    run(ctx) {
      const entries = ctx.subfolderEntries?.['scripts'];
      if (!entries) return { status: 'skipped', message: 'No scripts/ folder.' };
      const tiny = entries.filter((e) => e.type === 'file' && (e.size ?? 0) < 100);
      if (tiny.length === 0) return { status: 'pass', message: `${entries.length} script(s), all substantive.` };
      return {
        status: 'warn',
        message: `${tiny.length} script(s) under 100 bytes: ${tiny.map((t) => t.name).join(', ')}.`,
        fix: 'Stub scripts confuse Claude. Either implement them or remove them.',
      };
    },
  },
  {
    id: 'pd.scripts-linked',
    category: 'progressiveDisclosure',
    severity: 'info',
    weight: 2,
    title: '`scripts/` is invoked from the body',
    appliesTo: 'github',
    run(ctx) {
      if (!hasDir(ctx, 'scripts')) {
        return { status: 'skipped', message: 'No scripts/ directory.' };
      }
      if (/scripts\/[A-Za-z0-9._-]+/.test(ctx.parsed.body)) {
        return { status: 'pass', message: 'Body references scripts/.' };
      }
      return {
        status: 'warn',
        message: '`scripts/` exists but SKILL.md never references it.',
        fix: 'Reference scripts explicitly, e.g. `Run python scripts/validate.py …`.',
      };
    },
  },
];
